import AddCircleIcon from '@mui/icons-material/AddCircle';

import React, { useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { IsActiveEnum } from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { SimpleAppBar } from '_lib/components/AppBar/SimpleAppBar';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { Search } from '_lib/components/Search/Search';
import { activeEnumOptions, SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Stack } from '_lib/components/Stack/Stack';

import { normalizers, useQueryParams } from '_lib/hooks/useQueryParams';

import { AppDrawerState, AppRouteContext } from '../App/app.types';
import { AuthContext } from '../auth/authContext';
import { appRoutePaths, getFullAppRoutePath, getGoBackState } from '../Nav/nav.types';
import { PagedList } from '../PagedList/PagedList';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';
import { ScrollableContentWrapper } from '../ScrollableContentWrapper/ScrollableContentWrapper';

import { UserListItem } from './UserListItem';

type UserListProps = {
  appDrawerState?: AppDrawerState;
};

const UserList = (props: UserListProps) => {
  const { appDrawerState } = props;

  const navigate = useNavigate();

  const api = useContext(ApiContext);
  const { authUserCacheResource, callCenterCacheResource } = useContext(AuthContext);
  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const userDeleteMutation = api.users.useDelete();
  const userReactivateMutation = api.users.useReactivate();

  const { params, setParam, searchParams } = useQueryParams({
    searchWords: { defaultValue: '' },
    isActive: { defaultValue: IsActiveEnum.True, normalize: normalizers.enum(Object.values(IsActiveEnum)) }
  });

  const usersQuery = api.users.useList(
    {
      params: {
        ...params,
        callCenterId: cachedCallCenterQuery.data?.id
      }
    },
    { enabled: !!cachedCallCenterQuery.data?.id }
  );

  if (usersQuery.error) {
    return <RouteErrorBoundary appBarTitle='Users' errorTitle='Error Fetching Users' />;
  }

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%' }}>
      <SimpleAppBar title='Users' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <ActionBar
        actions={
          <Button
            disableFocusRipple
            text='Add User'
            variant='text'
            onClick={() => {
              navigate(`${getFullAppRoutePath(appRoutePaths.User)}/new`, {
                state: { goBack: getGoBackState(appRoutePaths.User, searchParams) }
              });
            }}
            startIcon={<AddCircleIcon />}
            sx={{ textTransform: 'none' }}
          />
        }
      />
      <Divider />
      <Stack direction='row' padding={2}>
        <SelectFilter
          label='Active/Inactive'
          initialKey={params.isActive}
          options={activeEnumOptions}
          onChange={(selectedKey) => setParam('isActive', selectedKey)}
          sx={{ width: '125px' }}
        />
        <Search initialValue={params.searchWords} onSearch={(searchWords) => setParam('searchWords', searchWords)} />
      </Stack>
      <Divider />
      <ScrollableContentWrapper
        isSavingOrLoading={
          usersQuery.isFetching ||
          cachedAuthUserQuery.isFetching ||
          userDeleteMutation.isPending ||
          userReactivateMutation.isPending
        }>
        <PagedList
          queryResult={usersQuery}
          renderFunction={(user) => (
            <UserListItem
              user={user}
              href={`${getFullAppRoutePath(appRoutePaths.User)}/${user.id}`}
              onClick={async (user) => {
                navigate(`${user.id}`, {
                  state: { goBack: getGoBackState(appRoutePaths.User, searchParams) }
                });
              }}
              onDelete={async (user) => {
                await userDeleteMutation.deleteItem({ id: user.id });
              }}
              onReactivate={async (user) => {
                await userReactivateMutation.reactivate({ id: user.id });
              }}
            />
          )}
        />
      </ScrollableContentWrapper>
    </FlexBox>
  );
};

export const UserListRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();
  return <UserList appDrawerState={appDrawerState} />;
};
